import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  fetchLiveOffer,
  offerEndsInWords,
  offerFrom,
  rememberOffer,
  type KnownOffer,
  type LiveOffer,
} from '../lib/offer.js';
import { Alert } from './ui.js';

/**
 * Keeps a listed `?offer=` from whatever address the app is opened at, for the
 * first Buy credits visit - C-133. Two addresses carry one: the sign-up link
 * from the website, and the confirmation link, which lands on `/sign-in` and is
 * sent straight on to the dashboard by `RequireAnonymous` before that page
 * could read anything. Mounted once, in `App`, so neither has to.
 */
export function useRememberOfferFromAddress(): void {
  const { search } = useLocation();
  useEffect(() => {
    const code = offerFrom(search);
    if (code !== null) rememberOffer(code);
  }, [search]);
}

/**
 * The server's answer for `code`, or null while it is asked and whenever the
 * offer is missing, inactive, expired or unknown. Null in, null out.
 */
export function useLiveOffer(code: KnownOffer | null): LiveOffer | null {
  const [offer, setOffer] = useState<LiveOffer | null>(null);

  useEffect(() => {
    if (code === null) return;
    let cancelled = false;
    void fetchLiveOffer(code).then((live) => {
      if (!cancelled) setOffer(live);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  return code === null ? null : offer;
}

/**
 * "LAUNCH-30: your 30% off is ready to use at checkout. Ends 31 October." -
 * C-133. The percentage and the date are the coupon's own, so an operator
 * edit on the Coupons page is what this says the next time it is read.
 */
export function OfferLine({ offer }: { offer: LiveOffer }): React.JSX.Element {
  const ends = offerEndsInWords(offer.endsAt);
  return (
    <div data-testid="offer-line">
      <Alert tone="success">
        <strong>
          {offer.code}: your {String(offer.percentOff)}% off is ready to use at checkout.
        </strong>
        {ends ? ` Ends ${ends}.` : null}
      </Alert>
    </div>
  );
}
